import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { CheckSquare } from 'lucide-react';
import { ReminderField, getBrowserTimezone } from '../components/ReminderField';
import { TaskList } from '../components/TaskList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TasksTab = () => {
  const { getAuthHeaders } = useAuth();
  const [recurringTasks, setRecurringTasks] = useState([]);
  const [todayTasks, setTodayTasks] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [taskType, setTaskType] = useState('recurring');
  const [editingTask, setEditingTask] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    reminder_offset: 'none',
  });

  const fetchTasks = useCallback(async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const [recurringRes, todayRes] = await Promise.all([
        axios.get(`${API}/tasks?task_type=recurring`, getAuthHeaders()),
        axios.get(`${API}/tasks?task_type=today&date=${today}`, getAuthHeaders()),
      ]);
      setRecurringTasks(recurringRes.data);
      setTodayTasks(todayRes.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }

    try {
      const today = new Date().toISOString().split('T')[0];

      const payload = {
        title: formData.title,
        description: formData.description,
        task_type: taskType,
        date: taskType === 'today' ? today : null,
        event_date: formData.event_date || '',
        event_time: formData.event_time || '',
        reminder_offset: formData.reminder_offset || 'none',
        timezone: getBrowserTimezone(),
      };

      if (editingTask) {
        await axios.put(`${API}/tasks/${editingTask.task_id}`, payload, getAuthHeaders());
        toast.success('Task updated!');
      } else {
        await axios.post(`${API}/tasks`, payload, getAuthHeaders());
        toast.success('Task created!');
      }
      fetchTasks();
      resetForm();
      setIsDialogOpen(false);
    } catch (error) {
      toast.error('Failed to save task');
    }
  };

  const toggleTaskComplete = async (task, isRecurring) => {
    try {
      await axios.put(`${API}/tasks/${task.task_id}`, { completed: !task.completed }, getAuthHeaders());
      if (isRecurring) {
        setRecurringTasks(tasks => tasks.map(t => t.task_id === task.task_id ? { ...t, completed: !t.completed } : t));
      } else {
        setTodayTasks(tasks => tasks.map(t => t.task_id === task.task_id ? { ...t, completed: !t.completed } : t));
      }
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const deleteTask = async (taskId, isRecurring) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`, getAuthHeaders());
      if (isRecurring) {
        setRecurringTasks(tasks => tasks.filter(t => t.task_id !== taskId));
      } else {
        setTodayTasks(tasks => tasks.filter(t => t.task_id !== taskId));
      }
      toast.success('Task deleted');
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const resetRecurringTasks = async () => {
    try {
      await axios.post(`${API}/tasks/reset-today`, {}, getAuthHeaders());
      setRecurringTasks(tasks => tasks.map(t => ({ ...t, completed: false })));
      toast.success('Tasks reset for today!');
    } catch (error) {
      toast.error('Failed to reset tasks');
    }
  };

  const initDMOTasks = async () => {
    try {
      const response = await axios.post(`${API}/tasks/init-dmo`, {}, getAuthHeaders());
      if (response.data.created > 0) {
        toast.success('DMO suggestions added!');
        fetchTasks();
      } else {
        toast.info('You already have DMO tasks');
      }
    } catch (error) {
      toast.error('Failed to add DMO suggestions');
    }
  };

  const moveTask = async (taskId, direction, isRecurring) => {
    const tasks = isRecurring ? recurringTasks : todayTasks;
    const currentIndex = tasks.findIndex(t => t.task_id === taskId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= tasks.length) return;
    
    // Swap tasks locally
    const newTasks = [...tasks];
    [newTasks[currentIndex], newTasks[newIndex]] = [newTasks[newIndex], newTasks[currentIndex]];
    
    // Update state immediately for smooth UX
    if (isRecurring) {
      setRecurringTasks(newTasks);
    } else {
      setTodayTasks(newTasks);
    }
    
    // Save new order to backend
    try {
      const taskIds = newTasks.map(t => t.task_id);
      await axios.post(`${API}/tasks/reorder`, { task_ids: taskIds }, getAuthHeaders());
    } catch (error) {
      // Revert on error
      if (isRecurring) {
        setRecurringTasks(tasks);
      } else {
        setTodayTasks(tasks);
      }
      toast.error('Failed to reorder tasks');
    }
  };

  const openEditDialog = (task, type) => {
    setEditingTask(task);
    setTaskType(type);
    setFormData({
      title: task.title,
      description: task.description || '',
      event_date: task.event_date || '',
      event_time: task.event_time || '',
      reminder_offset: task.reminder_offset || 'none',
    });
    setIsDialogOpen(true);
  };

  const openNewDialog = (type) => {
    resetForm();
    setTaskType(type);
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      event_date: '',
      event_time: '',
      reminder_offset: 'none',
    });
    setEditingTask(null);
  };

  return (
    <div className="p-4 space-y-4 max-w-2xl mx-auto" data-testid="tasks-tab">
      {/* Header */}
      <div className="text-center py-4">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-2xl mb-3">
          <CheckSquare className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
        </div>
        <h1 className="text-2xl font-outfit font-bold text-emerald-600 dark:text-emerald-400">Tasks</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">
          Stay focused on what matters most. These are your power moves for your daily method of operations.
        </p>
      </div>

      {/* Daily Method of Operations (DMO) */}
      <TaskList
        tasks={recurringTasks}
        type="recurring"
        title="Daily Method of Operations (DMO)"
        description="Repeat every day 🔄"
        color="emerald"
        onInitDMO={initDMOTasks}
        onReset={resetRecurringTasks}
        onOpenNewDialog={openNewDialog}
        onMoveTask={moveTask}
        onToggleComplete={toggleTaskComplete}
        onDeleteTask={deleteTask}
        onOpenEditDialog={openEditDialog}
      />

      {/* Today's Tasks */}
      <TaskList
        tasks={todayTasks}
        type="today"
        title="Today's Tasks"
        description="Just tasks for today 📋"
        color="emerald"
        onInitDMO={initDMOTasks}
        onReset={resetRecurringTasks}
        onOpenNewDialog={openNewDialog}
        onMoveTask={moveTask}
        onToggleComplete={toggleTaskComplete}
        onDeleteTask={deleteTask}
        onOpenEditDialog={openEditDialog}
      />

      {/* Task Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        setIsDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingTask ? 'Edit Task' : `Add ${taskType === 'recurring' ? 'Daily Recurring' : "Today's"} Task`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Task Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="What needs to be done?"
                data-testid="task-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Add more details..."
                data-testid="task-description-input"
              />
            </div>
            <ReminderField
              value={{ date: formData.event_date, time: formData.event_time, offset: formData.reminder_offset }}
              onChange={(v) => setFormData({ ...formData, event_date: v.date, event_time: v.time, reminder_offset: v.offset })}
              accent="emerald"
              dateLabel="Task date"
              timeLabel="Task time"
              testPrefix="task-reminder"
            />
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-task-btn">
                {editingTask ? 'Update Task' : 'Create Task'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TasksTab;
